import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Message } from "primereact/message";

import {
  ContractChase,
  chaseSummary,
} from "../../../../utils/contractChase";
import { NextStepAction } from "../../../../utils/prospectNextStep";
import {
  ContractTimelineEvent,
  LogDelayReasonPayload,
  LogReminderPayload,
} from "../../../../types/onboarding";
import ContractTimeline from "./ContractTimeline";
import LogReminderDialog from "./LogReminderDialog";
import LogDelayReasonDialog from "./LogDelayReasonDialog";

interface ContractActivityProps {
  // The timeline is owned by the page: the chase rule in the header reads the
  // same reminders this section lists, and fetching them twice would let the two
  // disagree.
  timeline: {
    events: ContractTimelineEvent[];
    logReminderAction: (payload: LogReminderPayload) => Promise<boolean>;
    logDelayAction: (payload: LogDelayReasonPayload) => Promise<boolean>;
  };
  chase?: ContractChase | null;
  // Set when the next-step button asks for this section's action.
  requestedAction?: NextStepAction | null;
  onRequestHandled?: () => void;
}

type OpenDialog = "none" | "reminder" | "delay";

/**
 * US4: contract activity section on the prospect detail page — the timeline
 * plus entry points to log a reminder or a delay reason.
 */
const ContractActivity: React.FC<ContractActivityProps> = ({
  timeline,
  chase = null,
  requestedAction = null,
  onRequestHandled,
}) => {
  const { events, logReminderAction, logDelayAction } = timeline;
  const [open, setOpen] = useState<OpenDialog>("none");
  const close = () => setOpen("none");

  useEffect(() => {
    if (requestedAction === "log-reminder") setOpen("reminder");
    if (requestedAction) onRequestHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedAction]);

  return (
    <div className="mb-4">
      <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <h3 className="m-0">Contract activity</h3>
        <div className="flex gap-2">
          <Button
            label="Log reminder"
            icon="pi pi-bell"
            severity="secondary"
            outlined
            onClick={() => setOpen("reminder")}
          />
          <Button
            label="Log delay reason"
            icon="pi pi-clock"
            severity="secondary"
            outlined
            onClick={() => setOpen("delay")}
          />
        </div>
      </div>

      {/* The same prompt the header carries, next to the button that answers it
          — an agent who came to this tab for something else should still see
          that the customer is waiting on a nudge. */}
      {chase?.due && (
        <Message
          severity="warn"
          className="w-full mb-3"
          text={`${chaseSummary(chase)} Reach out to the customer, then log the reminder here — nothing is sent from the Back Office.${
            chase.reasonUnknown
              ? " If you already know what the hold-up is, log a delay reason instead."
              : ""
          }`}
        />
      )}

      <ContractTimeline events={events} />

      <LogReminderDialog
        visible={open === "reminder"}
        onHide={close}
        onSubmit={logReminderAction}
      />
      <LogDelayReasonDialog
        visible={open === "delay"}
        onHide={close}
        onSubmit={logDelayAction}
      />
    </div>
  );
};

export default ContractActivity;
